import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import {
  Alert,
  Button,
  Flex,
  Heading,
  Loader,
  SelectField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  View,
} from '@aws-amplify/ui-react';
import { getDesignationsUrl } from './config';

// UI-only admin check; the backend re-enforces from the Cognito claims.
const ADMIN_DOMAINS = ['capitoldataanalytics.com', 'individualgiving.com', 'philanthropyops.com'];

async function authHeader() {
  const session = await Auth.currentSession();
  return { Authorization: session.getIdToken().getJwtToken() };
}

function CashRevenue({ user }) {
  const email = user?.attributes?.email || '';
  const callerDomain = email.split('@').pop().toLowerCase();
  const isAdmin = ADMIN_DOMAINS.includes(callerDomain);

  const [domains, setDomains] = useState([]);        // admin-selectable domains
  const [client, setClient] = useState(isAdmin ? '' : callerDomain);
  const [rows, setRows] = useState([]);              // {gift_type, cash, revenue, designated}
  const [bootLoading, setBootLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Bootstrap: identity + (admin) selectable domains.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeader();
        const res = await axios.get(getDesignationsUrl(), { headers });
        if (cancelled) return;
        const list = res.data.domains || [];
        setDomains(list);
        if (isAdmin && list.length && !client) setClient(list[0]);
      } catch (e) {
        if (!cancelled) setError(readError(e));
      } finally {
        if (!cancelled) setBootLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRows = useCallback(async (targetClient) => {
    if (!targetClient) return;
    setRowsLoading(true);
    setError('');
    setNotice('');
    try {
      const headers = await authHeader();
      const res = await axios.get(getDesignationsUrl(), {
        headers,
        params: { client: targetClient },
      });
      setRows((res.data.rows || []).map((r) => ({
        gift_type: r.gift_type,
        cash: r.cash_flag === 'Y',
        revenue: r.revenue_flag === 'Y',
        designated: !!r.designated,
      })));
    } catch (e) {
      setRows([]);
      setError(readError(e));
    } finally {
      setRowsLoading(false);
    }
  }, []);

  useEffect(() => { loadRows(client); }, [client, loadRows]);

  const setFlag = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value === 'True' } : r)));
    setNotice('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const headers = await authHeader();
      await axios.post(
        getDesignationsUrl(),
        {
          client,
          rows: rows.map((r) => ({
            gift_type: r.gift_type,
            cash_flag: r.cash ? 'Y' : 'N',
            revenue_flag: r.revenue ? 'Y' : 'N',
          })),
        },
        { headers },
      );
      // After a save every gift type is now designated.
      setRows((prev) => prev.map((r) => ({ ...r, designated: true })));
      setNotice('Saved. Dashboards are refreshing — updated figures appear once the extracts finish.');
    } catch (e) {
      setError(readError(e));
    } finally {
      setSaving(false);
    }
  };

  if (bootLoading) {
    return <Flex justifyContent="center" padding="2rem"><Loader size="large" /></Flex>;
  }

  return (
    <View maxWidth="900px" margin="0 auto" padding="1rem" textAlign="left">
      <Heading level={3}>Cash / Revenue Designation</Heading>
      <Text marginBottom="1rem">
        Mark each gift type as <strong>cash</strong> and/or <strong>revenue</strong>. Click
        <strong> Save </strong>to apply and refresh the dashboards.
      </Text>

      {isAdmin && (
        <SelectField
          label="Client"
          value={client}
          onChange={(e) => setClient(e.target.value)}
          marginBottom="1rem"
        >
          {domains.length === 0 && <option value="">No clients with gift data</option>}
          {domains.map((d) => <option key={d} value={d}>{d}</option>)}
        </SelectField>
      )}
      {!isAdmin && (
        <Text marginBottom="1rem"><strong>Client:</strong> {client}</Text>
      )}

      {error && <Alert variation="error" marginBottom="1rem">{error}</Alert>}
      {notice && <Alert variation="success" marginBottom="1rem">{notice}</Alert>}

      {rowsLoading ? (
        <Flex justifyContent="center" padding="2rem"><Loader size="large" /></Flex>
      ) : rows.length === 0 ? (
        <Alert variation="info">
          No gift types found for this client. Once gift data is uploaded, its gift types will
          appear here for designation.
        </Alert>
      ) : (
        <>
          <Table highlightOnHover variation="striped">
            <TableHead>
              <TableRow>
                <TableCell as="th">Gift type</TableCell>
                <TableCell as="th">Cash</TableCell>
                <TableCell as="th">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.gift_type}>
                  <TableCell>{r.gift_type}</TableCell>
                  <TableCell>
                    <SelectField
                      label={`cash-${r.gift_type}`}
                      labelHidden
                      value={r.cash ? 'True' : 'False'}
                      onChange={(e) => setFlag(i, 'cash', e.target.value)}
                    >
                      <option value="False">False</option>
                      <option value="True">True</option>
                    </SelectField>
                  </TableCell>
                  <TableCell>
                    <SelectField
                      label={`revenue-${r.gift_type}`}
                      labelHidden
                      value={r.revenue ? 'True' : 'False'}
                      onChange={(e) => setFlag(i, 'revenue', e.target.value)}
                    >
                      <option value="False">False</option>
                      <option value="True">True</option>
                    </SelectField>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Flex justifyContent="flex-end" marginTop="1rem">
            <Button variation="primary" onClick={save} isLoading={saving} isDisabled={saving}>
              Save
            </Button>
          </Flex>
        </>
      )}
    </View>
  );
}

function readError(e) {
  const status = e?.response?.status;
  if (status === 401) return 'Your session is not authorized. Try signing out and back in.';
  if (status === 403) return 'You are not authorized to view or edit this client.';
  return e?.response?.data?.error || e.message || 'Something went wrong.';
}

export default CashRevenue;
